/**
 * Truncate text to a specified length and indicate if truncation occurred
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 120)
 * @returns Object with truncated text and truncation status
 */
export const truncateText = (
  text: string,
  maxLength: number = 120,
): { truncated: string; isTruncated: boolean } => {
  if (text.length <= maxLength) {
    return { truncated: text, isTruncated: false };
  }
  return {
    truncated: text.substring(0, maxLength) + '...',
    isTruncated: true,
  };
};

/**
 * Truncate a filename while preserving the file extension
 * @param filename - The filename to truncate (e.g., "very_long_report_name.xlsx")
 * @param maxLength - Maximum total length including extension (default: 20)
 * @returns Truncated filename with extension preserved (e.g., "very_long_re....xlsx")
 */
export const truncateFilename = (
  filename: string,
  maxLength: number = 20,
): string => {
  if (filename.length <= maxLength) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf('.');

  // No extension found, truncate normally
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  const baseName = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex); // includes the dot

  // If extension is too long, just truncate the whole thing
  if (extension.length >= maxLength - 4) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  // Calculate how much space we have for the base name
  // Reserve space for "..." (3 chars) and the extension
  const maxBaseLength = maxLength - 3 - extension.length;

  if (maxBaseLength <= 0) {
    return filename.substring(0, maxLength - 3) + '...';
  }

  return baseName.substring(0, maxBaseLength) + '...' + extension;
};
